import { safeLoader } from "@/lib/safe-loader";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { createFileRoute } from "@tanstack/react-router";
import { resolveSeo, siteContentQuery, useContent } from "@/lib/site-content";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import preschoolReading from "../../assets/preschool-play.webp";
import preschoolNumbers from "../../assets/preschool-numbers.webp";

export const Route = createFileRoute("/es/preescolar")({
  loader: async ({ context }) => {
    const data = await safeLoader("es.preescolar.content", () => context.queryClient.ensureQueryData(siteContentQuery), undefined);
    return { seo: resolveSeo(data?.content, "forskola", "es") };
  },
  head: ({ loaderData }) => {
    const title = loaderData?.seo.title ?? "";
    const description = loaderData?.seo.description ?? "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: absoluteUrl("/es/preescolar") },
        { name: "twitter:card", content: "summary_large_image" },
        ...ogImage("/og/forskola-v1.jpg"),
        { property: "og:locale", content: "es_ES" },
      ],
      links: seoLinks("/es/preescolar"),
    };
  },
  component: Preescolar,
});

function Preescolar() {
  const { text } = useContent("forskola");

  return (
    <>
      <PageHero
        eyebrow={text("hero.eyebrow")}
        title={text("hero.title")}
        intro={text("hero.intro")}
      />

      <Prose>
        <img
          src={preschoolReading}
          alt="Una educadora lee un libro junto a los niños en el preescolar"
          loading="lazy"
          className="rounded-xl shadow-soft"
          width={1264}
          height={848}
        />

        <p>
          En España los niños empiezan antes a practicar las letras, la lectura, la escritura y las
          matemáticas. Nosotros seguimos el currículo sueco de preescolar, pero también queremos que
          los niños tengan la oportunidad de acercarse a estos aprendizajes tempranos de forma
          pedagógica y lúdica. En Swedish International School los niños conocen y practican
          español e inglés ya en preescolar, porque trabajamos con ambos idiomas de manera natural
          además del sueco. El colegio puede ofrecer dos grupos de preescolar –uno sueco y otro
          internacional– y está a solo 5–10 minutos de Marbella.
        </p>
        <p>
          De 9:00 a 15:00 recibimos a niños a partir de 3 años para una jornada divertida y
          educativa en un entorno seguro.
        </p>
        <p>
          El preescolar admite niños a partir de los 3 años. Si vuestro hijo todavía no ha cumplido
          3, podéis escribirnos igualmente: de forma excepcional, y solo tras una valoración
          individual junto con la familia, podríamos ofrecer plaza desde los 2 años. No es algo que
          podamos prometer de antemano.
        </p>

        <h2>El juego y el desarrollo social en el centro</h2>
        <p>
          En el preescolar sueco de Swedish International School el juego y el desarrollo social son
          lo principal. Las educadoras se centran en que los niños puedan explorar y descubrir su
          entorno a través del juego y de distintas actividades. Los niños también desarrollan sus
          capacidades lingüísticas, motoras y cognitivas.
        </p>

        <img
          src={preschoolNumbers}
          alt="Niños trabajando con números y materiales en el preescolar"
          loading="lazy"
          className="rounded-xl shadow-soft"
          width={810}
          height={540}
        />

        <h2>Continuidad educativa</h2>
        <p>
          Una ventaja de asistir a un preescolar sueco en Swedish International School es la
          continuidad educativa. El preescolar tiene el sueco como lengua principal y sigue el
          currículo sueco, lo que da a los alumnos una base estable para su escolarización
          posterior. Los niños se familiarizan pronto con el sistema escolar sueco y se adaptan con
          más facilidad si regresan a Suecia o continúan en otros colegios internacionales.
        </p>

        <h2>Tradiciones y cultura suecas</h2>
        <p>
          Las educadoras utilizan canciones, bailes y manualidades para acercar a los niños a las
          tradiciones y celebraciones suecas. Así los niños conocen su herencia cultural y se
          sienten parte de las tradiciones suecas aunque vivan en España.
        </p>
        <p>
          Para las familias que buscan una educación en sueco, el preescolar es una buena opción. El
          personal habla sueco y el sueco es la lengua principal, de modo que los niños mantienen y
          desarrollan su idioma mientras viven la experiencia única de crecer en otra cultura.
        </p>

        <h2>La jornada en el preescolar</h2>
        <p>
          El día tiene un ritmo claro: asamblea, actividades en grupos pequeños, juego al aire
          libre, comida y descanso. Los niños pasan buena parte del día fuera cuando el tiempo lo
          permite, y dentro hay espacio para manualidades, juego de construcción, cuentos y momentos
          de calma. El preescolar abre de 9:00 a 15:00 los días lectivos y sigue el{" "}
          <a className="text-primary link-quiet" href="/es/calendario-escolar">calendario escolar</a> del colegio.
        </p>

        <h2>Los idiomas en el día a día</h2>
        <p>
          El sueco es la base del entorno, mientras que el español y el inglés aparecen con
          naturalidad en canciones, juegos y conversaciones. Para los niños que hablan sueco como
          segunda lengua en casa, esto significa mucho idioma cotidiano con adultos suecoparlantes;
          y para los que se quedan en España, una introducción temprana y segura al español.
        </p>

        <h2>Cuestiones prácticas antes de empezar</h2>
        <ul>
          <li>La adaptación se planifica con vosotros, según las necesidades del niño.</li>
          <li>Traed ropa de cambio, protección solar, gorra y una botella de agua marcada.</li>
          <li>
            La comida y la merienda se sirven en el colegio; las dietas especiales se indican en la
            solicitud.
          </li>
          <li>Las ausencias por enfermedad se avisan por la mañana.</li>
        </ul>

        <h2>Plazas y tarifas</h2>
        <p>
          Las plazas se asignan de forma continua y son limitadas, así que conviene contactar con
          tiempo antes del periodo deseado. Las tarifas del preescolar están en la{" "}
          <a className="text-primary link-quiet" href="/es/tarifas">página de tarifas</a> y la inscripción se
          hace mediante el{" "}
          <a className="text-primary link-quiet" href="/es/solicitud">formulario de solicitud</a>. Escribidnos si
          tenéis dudas sobre edades, grupos o adaptación.
        </p>
      </Prose>

      <FaqBlock items={faqFor("forskola", "es")} locale="es" />

      <CtaBand locale="es" />
      <div className="h-20" />
    </>
  );
}
